// src/components/dashboard/InsuranceDonutChart.js
import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme
} from '@mui/material';

// Stable, type-bound colors (matches gauges)
const TYPE_COLORS = {
  Auto: '#2196f3',
  Home: '#ff9800',
  Business: '#4caf50',
  Life: '#f44336'
};

const STANDARD_TYPES = ['Auto', 'Home', 'Business', 'Life'];

export default function InsuranceDonutChart({
  height = 360,
  selectedEvent,
  data: propData,
  submissions = [],
  showLegend = true
}) {
  const theme = useTheme();
  const [activeName, setActiveName] = useState(null);

  const processedData = useMemo(() => {
    let base = Array.isArray(propData) && propData.length ? propData : null;

    if (!base) {
      const counts = {};
      let total = 0;
      submissions.forEach(sub => {
        if (Array.isArray(sub.insuranceTypes)) {
          sub.insuranceTypes.forEach(i => {
            const raw = typeof i === 'string' ? i : i?.type;
            if (!raw) return;
            const name = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
            counts[name] = (counts[name] || 0) + 1;
            total++;
          });
        }
      });
      base = Object.entries(counts).map(([name, leads]) => ({
        name,
        leads,
        value: total > 0 ? Math.round((leads / total) * 100) : 0
      }));
    }

    const map = new Map(base.map(d => [d.name, d]));
    return STANDARD_TYPES.map(name => map.get(name) || { name, value: 0, leads: 0 });
  }, [propData, submissions]);

  const totalLeads = processedData.reduce((s, d) => s + (d.leads || 0), 0);
  const hasData = totalLeads > 0;
  const active = processedData.find(d => d.name === activeName) || null;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #2196f3, #ff9800, #4caf50, #f44336)'
        }
      }}
    >
      <CardContent sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2196f3, #ff9800)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                boxShadow: '0 3px 8px rgba(33, 150, 243, 0.3)'
              }}
            >
              📊
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', fontSize: '1.1rem' }}>
                Insurance Distribution
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {hasData ? `${totalLeads} total leads` : 'No leads yet'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
          {hasData ? (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={processedData}
                    cx="50%"
                    cy="50%"
                    innerRadius="45%"
                    outerRadius="90%"
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    stroke="none"
                    label={false} // disable labels
                    onMouseEnter={(_, idx) => setActiveName(processedData[idx]?.name)}
                    onMouseLeave={() => setActiveName(null)}
                  >
                    {processedData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={TYPE_COLORS[entry.name] || '#ccc'}
                        tooltip={null} // disable native title tooltip
                        style={{
                          filter: activeName === entry.name ? 'brightness(1.1) drop-shadow(0 0 8px rgba(0,0,0,0.3))' : 'none',
                          transform: activeName === entry.name ? 'scale(1.05)' : 'scale(1)',
                          transformOrigin: 'center',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {/* Center display */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '50%',
                  width: 110,
                  height: 110,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {active ? (
                  <>
                    <Typography variant="h5" fontWeight={800} sx={{ color: TYPE_COLORS[active.name], lineHeight: 1 }}>
                      {active.leads}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                      {active.name}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h5" fontWeight={800} sx={{ color: theme.palette.primary.main, lineHeight: 1 }}>
                      {totalLeads}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                      Total
                    </Typography>
                  </>
                )}
              </Box>
            </>
          ) : (
            // No Data State
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  border: '3px dashed rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  fontSize: '2rem'
                }}
              >
                📊
              </Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                No Data Available
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                Submit your first lead to see the distribution
              </Typography>
            </Box>
          )}
        </Box>

        {/* Legend */}
        {showLegend && (
          <Box
            sx={{
              mt: 1.5,
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: 1
            }}
          >
            {STANDARD_TYPES.map((name) => {
              const item = processedData.find(d => d.name === name);
              return (
                <Box
                  key={name}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.75,
                    borderRadius: 1,
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '2px',
                      backgroundColor: TYPE_COLORS[name] || '#ccc'
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {item?.leads ?? 0}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
